import { Link } from "react-router-dom";
import { Navbar, Footer } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Home, Building2, Calculator, ArrowRight, Star, 
  Phone, Shield, Clock, Users
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_de12da21-0869-4a9b-8efe-c2e2b4d72064/artifacts/km3uhq32_YGK-CONSTRUCTION.png";

const HomePage = () => {
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section with Logo Background */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundColor: '#000000',
        }}
      >
        {/* Logo as full-screen background with low opacity */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            backgroundImage: `url(${LOGO_URL})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            filter: 'grayscale(30%)',
          }}
        />
        
        {/* Gradient overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black opacity-50" />
        
        {/* Orange glow effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
          <div className="stagger-children">
            {/* Prominent Logo */}
            <div className="mb-8">
              <img 
                src={LOGO_URL} 
                alt="YGK Construction - Your Good Karma" 
                className="h-48 md:h-64 lg:h-80 w-auto mx-auto logo-pulse"
              />
            </div>
            
            <p className="text-orange-500 font-semibold tracking-wider uppercase mb-4 text-lg">
              Oklahoma City's Trusted Builder
            </p>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white tracking-tight leading-none mb-6">
              Building Your Vision
              <span className="block text-gradient-orange">Into Reality</span>
            </h1>
            
            <p className="text-xl text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Professional construction services for residential and commercial projects. 
              Quality craftsmanship, transparent pricing, and results that last a lifetime.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/estimator">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold text-lg px-8 py-6 btn-glow transition-all">
                  Get Free Estimate
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button size="lg" variant="outline" className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-semibold text-lg px-8 py-6 transition-all">
                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-orange-500/50 rounded-full flex items-start justify-center pt-2">
            <div className="w-1.5 h-3 bg-orange-500/80 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-black text-white py-12 border-y border-orange-500/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-black text-orange-500 mb-2">15+</div>
              <p className="text-gray-500 text-sm uppercase tracking-wider">Years Experience</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-orange-500 mb-2">500+</div>
              <p className="text-gray-500 text-sm uppercase tracking-wider">Projects Completed</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-orange-500 mb-2">100%</div>
              <p className="text-gray-500 text-sm uppercase tracking-wider">Licensed & Insured</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-orange-500 mb-2">4.9★</div>
              <p className="text-gray-500 text-sm uppercase tracking-wider">Customer Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 bg-zinc-950 bg-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-orange-500 font-semibold tracking-wider uppercase mb-3">What We Do</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
              Our Services
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-zinc-900/50 border-2 border-zinc-800 hover:border-orange-500 transition-all duration-300 card-hover backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-orange-500/20 flex items-center justify-center mb-6">
                  <Home className="h-7 w-7 text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Residential</h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Custom homes, renovations, additions, and complete remodels. 
                  We bring your dream home to life with precision and care.
                </p>
                <Link to="/services" className="text-orange-500 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                  Learn More <ArrowRight className="h-4 w-4" />
                </Link>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-2 border-zinc-800 hover:border-orange-500 transition-all duration-300 card-hover backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-orange-500/20 flex items-center justify-center mb-6">
                  <Building2 className="h-7 w-7 text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Commercial</h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Office fit-outs, retail spaces, restaurants, and industrial buildings. 
                  Professional results that meet your business needs.
                </p>
                <Link to="/services" className="text-orange-500 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                  Learn More <ArrowRight className="h-4 w-4" />
                </Link>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-2 border-zinc-800 hover:border-orange-500 transition-all duration-300 card-hover backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-orange-500/20 flex items-center justify-center mb-6">
                  <Calculator className="h-7 w-7 text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Estimating</h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Accurate material take-offs and cost estimates. Know your project costs 
                  upfront with our transparent pricing tool.
                </p>
                <Link to="/estimator" className="text-orange-500 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                  Get Estimate <ArrowRight className="h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-orange-500 font-semibold tracking-wider uppercase mb-3">Why Choose YGK</p>
              <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
                Quality You Can <span className="text-orange-500">Trust</span>
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed mb-10">
                For over 15 years, we've been Oklahoma City's go-to construction partner. 
                Our commitment to quality, transparency, and customer satisfaction 
                sets us apart from the competition.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">Licensed & Insured</h4>
                    <p className="text-gray-400">Full coverage for your peace of mind on every project.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">On-Time Delivery</h4>
                    <p className="text-gray-400">We respect deadlines and deliver projects on schedule.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">Expert Team</h4>
                    <p className="text-gray-400">Skilled craftsmen with decades of combined experience.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 to-orange-600/20 blur-2xl" />
              <img 
                src="https://images.unsplash.com/photo-1630683924997-fe27050a0416?w=800&q=80"
                alt="Construction team at work"
                className="relative w-full h-[500px] object-cover border-2 border-orange-500/30"
              />
              <div className="absolute -bottom-8 -left-8 bg-orange-500 text-white p-8 max-w-xs">
                <div className="text-4xl font-black mb-2">500+</div>
                <p className="text-orange-100">Projects completed across the Oklahoma City region</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-zinc-950 bg-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-orange-500 font-semibold tracking-wider uppercase mb-3">Testimonials</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
              What Our Clients Say
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Sarah & Michael Thompson",
                project: "Kitchen Renovation",
                quote: "YGK transformed our dated kitchen into a modern masterpiece. The attention to detail was incredible, and they finished on time and on budget."
              },
              {
                name: "James Patterson",
                project: "Commercial Office Build",
                quote: "Professional from start to finish. The team at YGK understood our business needs and delivered a space that our employees love working in."
              },
              {
                name: "Emily Chen",
                project: "Home Addition",
                quote: "Adding a second story to our home seemed daunting, but YGK made it seamless. Their communication throughout the project was exceptional."
              }
            ].map((testimonial, index) => (
              <Card key={index} className="bg-zinc-900/50 border border-zinc-800 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-orange-500 text-orange-500" />
                    ))}
                  </div>
                  <p className="text-gray-300 leading-relaxed mb-6 italic">
                    "{testimonial.quote}"
                  </p>
                  <div>
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-sm text-orange-500">{testimonial.project}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-black border-t border-orange-500/20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <img 
            src={LOGO_URL} 
            alt="YGK Construction" 
            className="h-24 w-auto mx-auto mb-8 opacity-80"
          />
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
            Ready to Start Your <span className="text-orange-500">Project?</span>
          </h2>
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
            Get a free, no-obligation estimate for your construction project. 
            Our team is ready to bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/estimator">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold text-lg px-8 py-6 btn-glow transition-all">
                Get Free Estimate
                <Calculator className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-semibold text-lg px-8 py-6 transition-all">
                <Phone className="mr-2 h-5 w-5" />
                Call Us Today
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
